<script setup lang="ts">
import StfCol from '@/ui/columns/StfCol.vue';
import StfChip from '@/ui/chip/StfChip.vue';
import {useProductStore} from '@/store/good';
import type Good from '@/core/model/good';
import type GoodClass from '@/core/model/good-class';
import {storeToRefs} from 'pinia';
import {computed, nextTick, onMounted, onUnmounted, ref, watch, type ComponentInstance} from 'vue';
import StfIcon from '@/ui/icon/StfIcon.vue';
import StfRow from '@/ui/columns/StfRow.vue';
import {useDebounceFn} from '@vueuse/core';

interface Props {
    goodClass: GoodClass
    index: number;
}

const props = defineProps<Props>();
const productStore = useProductStore();
const {goods} = storeToRefs(productStore);
const showAll = ref(false);

const containerRef = ref<ComponentInstance<typeof StfRow> | null>(null);
/** Cutoff index (show suggestions [0, foundIndex)). Measured with all chips in layout (see isLayoutMeasuring). */
const minimumGoodsToDisplay = ref(0);
/** While true, every chip stays in layout so flex wrap / offsetTop measurement is correct (display:none breaks this). */
const isLayoutMeasuring = ref(true);
/** Hide chips visually until measurement + visibility apply, avoiding a visible two-phase flash. */
const chipsLayoutReady = ref(false);

let lastInnerWidth = typeof window !== 'undefined' ? window.innerWidth : 0;

const WIDTH_CHANGE_THRESHOLD_PX = 12;

const calculateCutoff = () => {
    if (!containerRef.value) {
        minimumGoodsToDisplay.value = 0;
        return;
    }
    const chips = containerRef.value.$el.children;
    if (chips.length === 0) {
        minimumGoodsToDisplay.value = 0;
        return;
    }

    let lineCount = 1;
    let firstChipTop = (chips[0] as HTMLElement).offsetTop;
    let foundIndex = chips.length;
    for (let i = 0; i < chips.length; i++) {
        if ((chips[i] as HTMLElement).offsetTop > firstChipTop) {
            lineCount++;
            firstChipTop = (chips[i] as HTMLElement).offsetTop;
        }

        if (lineCount === 4) {
            foundIndex = i;
            break;
        }
    }
    minimumGoodsToDisplay.value = foundIndex;
};

const finishMeasuring = async () => {
    isLayoutMeasuring.value = false;
    await nextTick();
    chipsLayoutReady.value = true;
};

const runMeasure = (): Promise<void> =>
    new Promise((resolve) => {
        chipsLayoutReady.value = false;
        isLayoutMeasuring.value = true;
        void nextTick().then(() => {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    calculateCutoff();
                    void finishMeasuring().then(resolve);
                });
            });
        });
    });

const debouncedRemeasureOnResize = useDebounceFn(() => {
    const w = window.innerWidth;
    if (Math.abs(w - lastInnerWidth) < WIDTH_CHANGE_THRESHOLD_PX) {
        return;
    }
    lastInnerWidth = w;
    void runMeasure();
}, 200);

const selectedGoodsInClass = computed(() => {
    return goods.value.filter(good => good.classNumber === props.goodClass.id).map(good => good.externalId);
});

const isItemVisible = (index: number) => {
    return index < (minimumGoodsToDisplay.value + selectedGoodsInClass.value.length);
};

/** Only remeasure when the chip set / order changes (new search, merged suggestions). Selection updates Pinia only — length can stay stable while `combinedClasses` recomputes, which used to retrigger measure and flash. */
const suggestionsLayoutSignature = computed(() =>
    props.goodClass.suggestions.map((g) => g.externalId).join('\0'),
);

onMounted(() => {
    void runMeasure().then(() => {
        lastInnerWidth = window.innerWidth;
    });
    window.addEventListener('resize', debouncedRemeasureOnResize);
});

onUnmounted(() => {
    window.removeEventListener('resize', debouncedRemeasureOnResize);
});

watch(suggestionsLayoutSignature, () => {
    void runMeasure();
});


const onChipClick = (good: Good) => {    
    productStore.toggleGood(good)
}

const toggleShowAll = () => {
    showAll.value = !showAll.value;
}

</script>


<template>
    <StfRow vcenter class="my-2">
        <StfCol>{{ goodClass.name }}</StfCol>
        <StfCol class="has-text-right" col="4" col-desktop="2">
            <StfRow @click="toggleShowAll" hcenter vcenter class="stf-clickable">
                <StfCol narrow>
                    <StfBody class="bulma-is-display-inline-block"><u>{{ showAll ? 'Hide' : 'Show More' }}</u></StfBody>
                </StfCol>
                <StfCol narrow>
                    <StfIcon :icon="showAll ? 'chevronUp': 'chevronDown'"/>
                </StfCol>
            </StfRow>
        </StfCol>
    </StfRow>
    <StfRow
        ref="containerRef"
        class="good-class-display__chips"
        :class="{ 'good-class-display__chips--pending': !chipsLayoutReady }"
    >
        <div
            v-for="(good, index) in goodClass.suggestions"
            v-show="isLayoutMeasuring || isItemVisible(index) || showAll"
            :key="good.externalId"
            class="m-05"
        >
            <StfChip full selectable @update:modelValue="onChipClick(good as Good)" :model-value="selectedGoodsInClass.includes(good.externalId)"  :text="good.name" bold/>
        </div>
    </StfRow>
</template>

<style scoped lang="scss">
.good-class-display__chips--pending {
    opacity: 0;
    pointer-events: none;
}
</style>
